import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tooltip,
  IconButton,
  Snackbar,
  Alert,
  Divider,
  Container,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import {
  AttachFile,
  InsertDriveFile,
  Close,
  CheckCircle,
  CloudUpload,
  FactCheck,
  Download,
} from "@mui/icons-material";
import "./DataUploadForm.css";
import gesLogo from "../FileUpload/ges-logos.png";
import { checkPasswordProtection, verifyPassword } from "../../api/upload";

const SF_UPLOAD_BASE_URL = process.env.REACT_APP_SF_UPLOAD_BASE_URL;
console.log("SF_UPLOAD_BASE_URL =", SF_UPLOAD_BASE_URL);

const DataUploadForm = () => {
  // --- Form State ---
  const [selectedFile, setSelectedFile] = useState(null);
  const [alphanumericId, setAlphanumericId] = useState("");
  const [sfUserEmail, setSfUserEmail] = useState("");
  const [isProtected, setIsProtected] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [idError, setIdError] = useState("");
  const [passwordVerified, setPasswordVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  // --- Validation & Dialog State ---
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [validationData, setValidationData] = useState(null);

  // --- Job & Progress State ---
  const [activeJobId, setActiveJobId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [jobResult, setJobResult] = useState(null);

  const fileInputRef = useRef(null);

  // --- Polling Logic ---
  useEffect(() => {
    let intervalId;

    if (activeJobId && isProcessing) {
      intervalId = setInterval(async () => {
        try {
          const response = await fetch(
            `${SF_UPLOAD_BASE_URL}/api/sfupload/status/${activeJobId}`
          );
          if (!response.ok) throw new Error("Status check failed");

          const data = await response.json();

          setUploadProgress(data.progress || 0);
          setCurrentStage(data.current_stage || "Processing...");

          if (data.status === "COMPLETED" || data.status === "FAILED") {
            setIsProcessing(false);
            setJobResult(data.result);
            clearInterval(intervalId);

            setSnackbar({
              open: true,
              message:
                data.status === "COMPLETED"
                  ? "Job Completed Successfully!"
                  : "Job Failed",
              severity: data.status === "COMPLETED" ? "success" : "error",
            });
          }
        } catch (error) {
          console.error("Polling error:", error);
        }
      }, 1000);
    }

    return () => clearInterval(intervalId);
  }, [activeJobId, isProcessing]);

  // --- Event Handlers ---

  const validateId = (value) => {
    const idRegex = /^[a-zA-Z0-9]{15,18}$/;
    if (!value) {
      setIdError("");
      return false;
    }
    if (!idRegex.test(value)) {
      setIdError("ID must be 15-18 alphanumeric characters");
      return false;
    }
    setIdError("");
    return true;
  };

  const handleIdChange = (event) => {
    setAlphanumericId(event.target.value);
    validateId(event.target.value);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setJobResult(null);
    setUploadProgress(0);
  };

  // --- STEP 1: REVIEW (validate) ---
  const handleReview = async () => {


    if (isProtected && !passwordVerified) {
        setSnackbar({
          open: true,
          message: "Please verify password first",
          severity: "warning",
        });
        return;
      }
    // 🔐 PASSWORD VALIDATION
    if (isProtected) {
      if (!password) {
        setPasswordError("Password required");
        return;
      }

      try {
        const verify = await verifyPassword(selectedFile, password);

        if (!verify.valid) {
          setPasswordError("Wrong password");
          return;
        }

        setPasswordError("");
      } catch (err) {
        setPasswordError("Password verification failed");
        return;
      }
    }
    if (!selectedFile)
      return setSnackbar({
        open: true,
        message: "Please select a file",
        severity: "warning",
      });
    if (!validateId(alphanumericId))
      return setSnackbar({
        open: true,
        message: "Invalid ID",
        severity: "warning",
      });

    try {
      setIsProcessing(true);
      setCurrentStage("Validating File...");

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("opportunity_id", alphanumericId);
      if (isProtected) {

        formData.append("password", password);
        }
      const response = await fetch(`${SF_UPLOAD_BASE_URL}/api/sfupload/validate`, {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      setIsProcessing(false);

      if (data.valid) {
        setValidationData(data);
        setConfirmOpen(true);
      } else {
        setSnackbar({
          open: true,
          message: "Validation Failed: " + data.opportunity_name,
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Validation error:", error);
      setIsProcessing(false);
      setSnackbar({
        open: true,
        message: "Server Validation Error",
        severity: "error",
      });
    }
  };



  const handleVerifyPassword = async () => {
  if (!password) {
    setPasswordError("Enter password");
    return;
  }

  setVerifying(true);

  try {
    const res = await verifyPassword(selectedFile, password);

    if (res.valid) {
      setPasswordVerified(true);
      setPasswordError("");
      setSnackbar({
        open: true,
        message: "Password verified successfully",
        severity: "success",
      });
    } else {
      setPasswordVerified(false);
      setPasswordError("Wrong password");
    }
  } catch (err) {
    setPasswordError("Verification failed");
  } finally {
    setVerifying(false);
  }
};

  // --- STEP 2: EXECUTE ---
  const handleConfirmUpload = async () => {
    setConfirmOpen(false);

    try {
      setIsProcessing(true);
      setUploadProgress(0);
      setCurrentStage("Queued for Processing...");
      setJobResult(null);

      const response = await fetch(`${SF_UPLOAD_BASE_URL}/api/sfupload/execute`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          temp_id: validationData.temp_id,
          opportunity_id: alphanumericId,
          executor_user_email: sfUserEmail || "",
        }),
      });

      const result = await response.json();

      if (response.ok) {
        console.log("Upload started, Job ID:", result.job_id);
        setActiveJobId(result.job_id);
      } else {
        throw new Error(result.detail || "Execution failed");
      }
    } catch (error) {
      console.error("Execution error:", error);
      setIsProcessing(false);
      setSnackbar({
        open: true,
        message: "Upload failed: " + error.message,
        severity: "error",
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " KB";
    return (bytes / (1024 * 1024)).toFixed(2) + " MB";
  };

  const isFormValid = selectedFile && !idError && alphanumericId.length >= 15;

  // --- Download Handler ---
const handleDownload = (type) => {
  if (!activeJobId) {
    setSnackbar({
      open: true,
      message: "Upload not completed yet",
      severity: "warning",
    });
    return;
  }

  const url = `${SF_UPLOAD_BASE_URL}/api/sfupload/download/${type}/${activeJobId}`;
  window.open(url, "_blank");
};

  return (
    <Box className="data-upload-container">
      <Container maxWidth="md">
        <Box
          component="img"
          src={gesLogo}
          alt="GES Logo"
          sx={{
            position: "absolute",
            top: 24,
            left: 24,
            height: 64,
            width: "auto",
            zIndex: 10,
            filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.1))",
          }}
        />

        <Box className="data-upload-wrapper">
          <Paper className="upload-paper">
            <Box className="header-section">
              <Typography variant="h4" className="main-title">
                Salesforce Data Upload Automation
              </Typography>
              <Typography variant="body1" className="subtitle">
                Upload your cleaned .xlsx file and enter a valid Opportunity ID.
              </Typography>
            </Box>

            <Box
              sx={{ mt: 2, mb: 3, display: "flex", justifyContent: "center" }}
            >
              <Link to="/" style={{ textDecoration: "none" }}>
                <Typography
                  sx={{
                    color: "#1B3B58",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  ← Back to Home
                </Typography>
              </Link>
            </Box>

            <Divider sx={{ my: 4, borderColor: "rgba(0,0,0,0.06)" }} />

            <Box className="form-content">
              {/* --- File Upload --- */}
              <Box className="form-section">
                <Typography variant="subtitle1" className="section-label">
                  File Upload
                </Typography>
                <Box className="file-upload-area">
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".xlsx,.xls,.csv"
                    onChange={async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const allowedExtensions = [".xlsx", ".xls", ".csv"];
                    const allowedMimeTypes = [
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                      "application/vnd.ms-excel",
                      "text/csv",
                    ];

                    const ext = file.name
                      .toLowerCase()
                      .substring(file.name.lastIndexOf("."));

                    if (
                      !allowedExtensions.includes(ext) ||
                      !allowedMimeTypes.includes(file.type)
                    ) {
                      setSnackbar({
                        open: true,
                        message:
                          "Only CSV or Excel files (.csv, .xlsx, .xls) are allowed.",
                        severity: "error",
                      });

                      if (fileInputRef.current)
                        fileInputRef.current.value = "";
                      return;
                    }

                    setSelectedFile(file);
                    setJobResult(null);
                    setUploadProgress(0);
                    setPassword("");
                    setPasswordError("");
                    setPasswordVerified(false);

                    // 🔥 NEW: CHECK PASSWORD
                    try {
                      const res = await checkPasswordProtection(file);
                      console.log("PASSWORD CHECK RESPONSE:", res);
                      if (res.protected) {
                        setIsProtected(true);
                        setSnackbar({
                          open: true,
                          message: "File is password protected. Enter password.",
                          severity: "info",
                        });
                      } else {
                        setIsProtected(false);
                      }
                    } catch (err) {
                      console.error("Password check error:", err);
                    }

                    setSnackbar({
                      open: true,
                      message: `Selected: ${file.name}`,
                      severity: "success",
                    });

                    if (fileInputRef.current)
                      fileInputRef.current.value = "";
                  }}
                    style={{ display: "none" }}
                  />

                  <Tooltip title="Attach File">
                    <Button
                      variant="contained"
                      onClick={() => fileInputRef.current?.click()}
                      className="attach-button"
                      startIcon={<AttachFile />}
                      disabled={isProcessing}
                    >
                      Choose File
                    </Button>
                  </Tooltip>
                </Box>
                {selectedFile && (
                  <Box className="selected-file">
                    <List sx={{ p: 0 }}>
                      <ListItem className="file-item">
                        <ListItemIcon>
                          <InsertDriveFile />
                        </ListItemIcon>
                        <Tooltip title={selectedFile.name} arrow>
                          <ListItemText
                            primary={selectedFile.name}
                            secondary={formatFileSize(selectedFile.size)}
                            primaryTypographyProps={{
                              fontWeight: 600,
                              sx: {
                                maxWidth: 280,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                        </Tooltip>
                        <IconButton
                          onClick={handleRemoveFile}
                          size="small"
                          disabled={isProcessing}
                        >
                          <Close />
                        </IconButton>
                      </ListItem>
                    </List>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 3 }} />
               {isProtected && !passwordVerified && (
              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  background: "#fff8dc",
                  borderRadius: 3,
                  border: "2px solid #DD7C25",
                }}
              >
                <Typography sx={{ mb: 1, fontWeight: 600 }}>
                  🔐 File is password protected
                </Typography>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <TextField
                    fullWidth
                    type="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={!!passwordError}
                    helperText={passwordError}
                  />

                  <Button
                    variant="contained"
                    onClick={handleVerifyPassword}
                    disabled={verifying}
                  >
                    {verifying ? "Verifying..." : "Verify"}
                  </Button>
                </Box>
              </Box>
            )}
              {/* --- Opportunity ID --- */}
              <Box className="form-section">
                <Typography variant="subtitle1" className="section-label">
                  Opportunity ID
                </Typography>
                <TextField
                  fullWidth
                  placeholder="006xxxxxxxxxxxxxxx"
                  value={alphanumericId}
                  onChange={handleIdChange}
                  error={!!idError}
                  helperText={
                    idError || "18-character Salesforce ID (starts with 006)"
                  }
                  disabled={isProcessing}
                  inputProps={{ maxLength: 18 }}
                />
                {alphanumericId && !idError && (
                  <Box
                    className="validation-success"
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mt: 1,
                      color: "success.main",
                    }}
                  >
                    <CheckCircle fontSize="small" sx={{ mr: 1 }} />
                    <Typography variant="body2">ID format valid</Typography>
                  </Box>
                )}
              </Box>
               
              {/* --- Optional User Email --- */}
              <Box className="form-section" sx={{ mt: 2 }}>
                <Typography variant="subtitle1" className="section-label">
                  User Email (Salesforce Email ID)
                </Typography>
                <TextField
                  fullWidth
                  placeholder="salesforce_integrations@ges.com"
                  value={sfUserEmail}
                  onChange={(e) => setSfUserEmail(e.target.value)}
                  helperText="Example: emeaboothupload_integration@ges.com"
                  disabled={isProcessing}
                />
              </Box>

              {/* --- Progress Bar --- */}
              {isProcessing && (
                <Box sx={{ mt: 4, mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="primary"
                      fontWeight={600}
                    >
                      {currentStage}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {uploadProgress}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* --- Action Button (Step 1) --- */}
              <Box className="button-section">
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleReview}
                  disabled={!isFormValid || isProcessing}
                  className="submit-button"
                  endIcon={isProcessing ? null : <FactCheck />}
                >
                  {isProcessing ? "Processing..." : "Review & Upload"}
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* --- RESULT SUMMARY BOX --- */}
          {jobResult && (
            <Card
              sx={{
                mt: 3,
                border: "1px solid #e0e0e0",
                boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <CheckCircle sx={{ color: "success.main", mr: 1 }} /> Upload
                  Summary
                </Typography>

                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "#f5f9ff",
                        borderRadius: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="primary.main"
                        fontWeight="bold"
                      >
                        {jobResult.total_processed}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Records Processed
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: "#fff5f5",
                        borderRadius: 2,
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        variant="h4"
                        color="error.main"
                        fontWeight="bold"
                      >
                        {jobResult.total_failed}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Records Failed
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Download Buttons */}
                <Box>
                  {/* Reduced spacing to 1 to ensure 4 buttons fit nicely in one row */}
                  <Grid container spacing={1} wrap="nowrap" alignItems="center">
                    
                    {/* 1. Successful Records */}
                    <Grid item sx={{ flex: "1 1 0" }}>
                      <Button
                        fullWidth
                        startIcon={<Download />}
                        onClick={() => handleDownload("success")}
                        sx={{
                          height: 64,
                          borderRadius: 3,
                          bgcolor: "#f5f9ff",
                          border: "1px solid #4caf50",
                          color: "#4caf50",
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": {
                            bgcolor: "#eef6f0",
                          },
                        }}
                      >
                        Successful Records
                      </Button>
                    </Grid>

                    {/* 2. Failed Records */}
                    <Grid item sx={{ flex: "1 1 0" }}>
                      <Button
                        fullWidth
                        startIcon={<Download />}
                        onClick={() => handleDownload("failed")}
                        sx={{
                          height: 64,
                          borderRadius: 3,
                          bgcolor: "#fff5f5",
                          border: "1px solid #f44336",
                          color: "#f44336",
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": {
                            bgcolor: "#fdecec",
                          },
                        }}
                      >
                        Failed Records
                      </Button>
                    </Grid>

                    {/* 3. Performance Summary */}
                    <Grid item sx={{ flex: "1 1 0" }}>
                      <Button
                        fullWidth
                        startIcon={<Download />}
                        onClick={() => handleDownload("summary")}
                        sx={{
                          height: 64,
                          borderRadius: 3,
                          bgcolor: "#f5f9ff",
                          border: "1px solid #2196f3",
                          color: "#2196f3",
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": {
                            bgcolor: "#eaf3ff",
                          },
                        }}
                      >
                        Performance Summary
                      </Button>
                    </Grid>

                    {/* 4. NEW: Retry Excel */}
                    <Grid item sx={{ flex: "1 1 0" }}>
                      <Button
                        fullWidth
                        startIcon={<Download />}
                        onClick={() => handleDownload("failed-excel")}
                        sx={{
                          height: 64,
                          borderRadius: 3,
                          bgcolor: "#fff8e1", // Amber background
                          border: "1px solid #ff9800", // Amber border
                          color: "#e65100", // Dark Amber text
                          fontWeight: 600,
                          textTransform: "none",
                          "&:hover": {
                            bgcolor: "#ffecb3",
                          },
                        }}
                      >
                        Retry Excel
                      </Button>
                    </Grid>

                  </Grid>
                </Box>
    

              </CardContent>
            </Card>
          )}

          {/* --- CONFIRMATION DIALOG (Step 2) --- */}
          <Dialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle
              id="alert-dialog-title"
              sx={{ bgcolor: "#f5f9ff", color: "#1B3B58" }}
            >
              {"Confirm Upload Details"}
            </DialogTitle>
            <DialogContent sx={{ mt: 2, minWidth: "400px" }}>
              <DialogContentText id="alert-dialog-description">
                Please verify the details below before proceeding to Salesforce.
              </DialogContentText>

              {validationData && (
                <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: "bold" }}
                      >
                        OPPORTUNITY NAME
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ color: "#1976d2", fontWeight: 500 }}
                      >
                        {validationData.opportunity_name}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: "bold" }}
                      >
                        DATA ROWS
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {validationData.row_count}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", fontWeight: "bold" }}
                      >
                        FILE NAME
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {validationData.filename}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                onClick={() => setConfirmOpen(false)}
                color="inherit"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUpload}
                variant="contained"
                color="primary"
                autoFocus
                startIcon={<CloudUpload />}
              >
                Start Upload
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};


export default DataUploadForm;
